import { Component, Vue, Prop } from 'vue-property-decorator';
import { Inject } from 'vue-di-container';
import WithRender from './ProviderUpdate.html';
import ProviderService from '../ProviderSupportService';
import { ProviderModel } from '../ProviderSupportModel';
import { Toaster } from '../../../Services/toast';
import ValidationService from '../../../Services/ValidationService';
import UserModel from '../../../shared/userService/Model/UserModel';
import UserService from '../../../shared/userService/UserService';
import UserRoleService from '../../../shared/userService/UserRoleService';
import { ProviderAttachmentCategoryType } from '@/shared/Enums/ProviderAttachmentCategoryType';

@WithRender
@Component({
  components: {},
  diProvide: [UserService, UserRoleService, ProviderService],
})
export default class ProviderUpdate extends Vue {

  private get providerRequiredCategoryIds(): string[] {
    return [
      ProviderAttachmentCategoryType.CstLicense,
      ProviderAttachmentCategoryType.CommercialRegistration,
      ProviderAttachmentCategoryType.DelegationLetter,
      ProviderAttachmentCategoryType.NationalAddress,
      ProviderAttachmentCategoryType.ZakatTaxCertificate,
    ];
  }
  private get hasAllProviderRequiredAttachments(): boolean {
    const uploaded: any = {};
    for (let i = 0; i < this.providerUploadedAttachments.length; i++) {
      uploaded[this.providerUploadedAttachments[i].categoryId] = true;
    }
    for (let j = 0; j < this.providerRequiredCategoryIds.length; j++) {
      if (!uploaded[this.providerRequiredCategoryIds[j]]) { return false; }
    }
    return true;
  }

  public get isValid(): boolean {
    return (this.provider.name.trim() !== '' &&
      this.provider.enterpriseUnifiedNumber.trim() !== '' &&
      this.provider.accreditationNumber.trim() !== '' &&
      this.provider.taxNumber.trim() !== '' &&
      this.validationService.isValidVatNumber(this.provider.taxNumber) &&
      this.hasAllProviderRequiredAttachments
    );
  }

  get canAddProviderAttachment(): boolean {
    const hasFile = !!this.selectedProviderFileName && !!this.selectedProviderFileContent;
    const hasCat = !!this.selectedProviderAttachmentCategory;
    const notDup =
      hasCat &&
      !this.providerUploadedAttachments.some((a) => a.categoryId === this.selectedProviderAttachmentCategory);
    return hasFile && hasCat && notDup;
  }
  @Inject(ProviderService) public providerService!: ProviderService;
  @Inject(ValidationService) public validationService!: ValidationService;
  @Inject(UserService) public userService!: UserService;
  @Inject(UserRoleService) public userRoleService!: UserRoleService;

  @Prop({ type: Boolean, default: false }) public layoutSup!: boolean;
  @Prop({ type: String, default: 'Edit' }) public mode!: string;
  @Prop({ default: '' }) public id!: string;

  public editProviderDialog: boolean = false;

  public provider: ProviderModel = {
    id: '',
    providerCode: '',
    name: '',
    createdDate: null,
    enterpriseUnifiedNumber: '',
    accreditationNumber: '',
    email: '',
    taxNumber: '',
    hasInternational: false,
    country: '',
    city: '',
    district: '',
    streetName: '',
    buildingNumber: '',
    postalCode: '',
    vatNumber: '',
    additionalNumber: '',
    otherIdentifier: '',
    balance: 0,
    creditLimitAmount: 0,
    bankAccountNumber: '',
    expireDate: '',
    state: '',
    isDisconnected: '',
    integrationBaseURL: '',
    integrationApiKey: '',
    providerAttachmentModel: [],
    providerRequestId: '',
    isVirtualOperator: false,
    virtualOperatorId: null,
    virtualOperatorName: '',
    crNumber: '',
  };

  public user: UserModel = {
    userId: '',
    username: '',
    details: {
      Email: '',
      MobilePhone: '',
      Operator: '',
      Provider: '',
      OperatorId: '',
      TenantId: '',
      ProviderId: '',
      Role: '',
      ChangePasswordRequired: '',
    },
  };

  public providerAttachmentCategoryList: Array<{ id: string; name: string }> = [
    { id: ProviderAttachmentCategoryType.CstLicense, name: this.$t('shared.cstLicense').toString() },
    { id: ProviderAttachmentCategoryType.CommercialRegistration, name: this.$t('shared.commercialRegistration').toString() },
    { id: ProviderAttachmentCategoryType.DelegationLetter, name: this.$t('shared.delegationLetter').toString() },
    { id: ProviderAttachmentCategoryType.NationalAddress, name: this.$t('shared.nationalAddress').toString() },
    { id: ProviderAttachmentCategoryType.ZakatTaxCertificate, name: this.$t('shared.zakatTaxCertificate').toString() },
  ];
  public deletedProviderAttachmentCategoryList: Array<{ id: string; name: string }> = [];

  public providerUploadedAttachments: Array<{
    id?: string;
    categoryId: string;
    categoryName: string;
    fileName: string;
    fileContent: string;
    fileSize: number;
    fileExt: string;
  }> = [];

  public selectedProviderFileName = '';
  public selectedProviderFileContent = '';
  public selectedProviderFileSize = 0;
  public selectedProviderFileExt = '';
  public selectedProviderAttachmentCategory = '';
  public replaceIndex: number | null = null;
  public providerFileBusy = false;

  public async mounted() {
    this.$emit('childinit', this.layoutSup);
    this.user = await this.userService.getUser();

    let providerId = '';
    if (this.id) { providerId = this.id; }
    else if ((this as any).$route && (this as any).$route.params && (this as any).$route.params.id) {
      providerId = (this as any).$route.params.id;
 }
    else if (this.user && this.user.details && this.user.details.ProviderId) {
      providerId = this.user.details.ProviderId;
 }

    if (!providerId) {
      Toaster.error(this.$t('shared.validationFailed').toString());
      return;
    }

    const resp = await this.providerService.GetById(providerId);
    const data = (resp && resp.result != null) ? resp.result : resp;

    const s = (v: any) => (v == null ? '' : String(v));
    const n = (v: any, def = 0) => (v == null ? def : v);
    const b = (v: any) => Boolean(v);

    this.provider = {
      ...this.provider,
      ...data,

      id: s(data.id),
      name: s(data.name),
      enterpriseUnifiedNumber: s(data.enterpriseUnifiedNumber),
      accreditationNumber: s(data.accreditationNumber),
      email: s(data.email),
      taxNumber: s(data.taxNumber),
      country: s(data.country),
      city: s(data.city),
      district: s(data.district),
      streetName: s(data.streetName),
      buildingNumber: s(data.buildingNumber),
      postalCode: s(data.postalCode),
      vatNumber: s(data.vatNumber),
      additionalNumber: s(data.additionalNumber),
      otherIdentifier: s(data.otherIdentifier),
      bankAccountNumber: s(data.bankAccountNumber),
      expireDate: s(data.expireDate),
      state: s(data.state),
      isDisconnected: s(data.isDisconnected),
      integrationBaseURL: s(data.integrationBaseURL),
      integrationApiKey: s(data.integrationApiKey),

      balance: n(data.balance, 0),
      creditLimitAmount: n(data.creditLimitAmount, 0),
      hasInternational: b(data.hasInternational),
      createdDate: (data.createdDate == null ? null : data.createdDate),

      providerAttachmentModel: Array.isArray(data.providerAttachmentModel)
        ? data.providerAttachmentModel
        : [],
      providerRequestId: s(data.providerRequestId),
      isVirtualOperator: b(data.isVirtualOperator),
      virtualOperatorId: data.virtualOperatorId == null ? null : data.virtualOperatorId,
      virtualOperatorName: s(data.virtualOperatorName),
    };

    const attachments = this.provider.providerAttachmentModel || [];
    for (let k = 0; k < attachments.length; k++) {
      const a = attachments[k] as { id?: string; fileName: string; fileContent: string; attachmentType: string; };
      const catName = this.categoryNameFor(a.attachmentType);
      this.providerUploadedAttachments.push({
        id: a.id,
        categoryId: a.attachmentType,
        categoryName: catName,
        fileName: a.fileName,
        fileContent: a.fileContent || '',
        fileSize: a.fileContent ? Math.floor((a.fileContent.length * 3) / 4) : 0,
        fileExt: ((a.fileName || '').split('.').pop() || '').toLowerCase(),
      });
      const inMain = this.providerAttachmentCategoryList.findIndex((x) => x.id === a.attachmentType);
      if (inMain !== -1) {
        const removed = this.providerAttachmentCategoryList.splice(inMain, 1)[0];
        this.deletedProviderAttachmentCategoryList.push(removed);
      }
    }
  }

  public onPickProviderFile() {
    (this.$refs as any).providerFile.value = '';
    (this.$refs as any).providerFile.click();
  }

  public onProviderFilePicked(event: any) {
    const files: FileList = event && event.target && event.target.files ? event.target.files : (null as any);
    if (!files || !files.length) {
      this.resetProviderFileSelection();
      this.replaceIndex = null;
      return;
    }
    if (this.providerFileBusy) { return; }
    this.providerFileBusy = true;

    try {
      const file = files[0];
      this.selectedProviderFileName = file.name;
      this.selectedProviderFileSize = file.size;
      this.selectedProviderFileExt = (file.name.split('.').pop() || '').toLowerCase();

      const reader = new FileReader();
      reader.onloadend = (e: any) => {
        try {
          const resultStr = e && e.target && e.target.result ? String(e.target.result) : '';
          const base64 = resultStr.indexOf(',') >= 0 ? resultStr.split(',')[1] : resultStr;

          if (typeof this.replaceIndex === 'number' && this.replaceIndex >= 0) {
            const existing = this.providerUploadedAttachments[this.replaceIndex];
            if (existing) {
              existing.fileName = file.name;
              existing.fileContent = base64;
              existing.fileSize = this.selectedProviderFileSize;
              existing.fileExt = this.selectedProviderFileExt;
            }
            this.replaceIndex = null;
            (this.$refs as any).providerFile.value = '';
            this.selectedProviderFileName = '';
            this.selectedProviderFileContent = '';
            this.selectedProviderFileSize = 0;
            this.selectedProviderFileExt = '';
            return;
          }

          this.selectedProviderFileContent = base64;
        } finally {
          this.providerFileBusy = false;
        }
      };
      reader.readAsDataURL(file);
    } catch {
      this.providerFileBusy = false;
      this.resetProviderFileSelection();
      this.replaceIndex = null;
      Toaster.error(this.$t('shared.validationFailed').toString());
    }
  }

  public addProviderAttachment() {
    const cat = this.providerAttachmentCategoryList.find((c) => c.id === this.selectedProviderAttachmentCategory);
    if (!cat) {
      Toaster.error(this.$t('shared.addCategory').toString());
      return;
    }

    const ext = (this.selectedProviderFileExt || '').toLowerCase();
    if (!(ext === 'pdf' || ext === 'doc' || ext === 'docx')) {
      Toaster.error(this.$t('shared.invalidFileType').toString());
      return;
    }
    if (this.selectedProviderFileSize > 3000000) {
      Toaster.error(this.$t('shared.theMaximumSizeForTheAttachmentIs3Megabytes').toString());
      return;
    }

    this.providerUploadedAttachments.push({
      categoryId: cat.id,
      categoryName: cat.name,
      fileName: this.selectedProviderFileName,
      fileContent: this.selectedProviderFileContent,
      fileSize: this.selectedProviderFileSize,
      fileExt: this.selectedProviderFileExt,
    });

    this.deletedProviderAttachmentCategoryList.push(cat);
    const idx = this.providerAttachmentCategoryList.findIndex((x) => x.id === cat.id);
    if (idx !== -1) { this.providerAttachmentCategoryList.splice(idx, 1); }

    this.selectedProviderAttachmentCategory = '';
    this.resetProviderFileSelection();
  }

  public deleteProviderAttachment(index: number) {
    const att = this.providerUploadedAttachments[index];
    if (!att) { return; }

    const deletedCat = this.deletedProviderAttachmentCategoryList.find((d) => d.id === att.categoryId);
    if (deletedCat) {
      this.providerAttachmentCategoryList.push(deletedCat);
      const delIdx = this.deletedProviderAttachmentCategoryList.findIndex((d) => d.id === att.categoryId);
      if (delIdx !== -1) { this.deletedProviderAttachmentCategoryList.splice(delIdx, 1); }
    }
    this.providerUploadedAttachments.splice(index, 1);
  }

  public replaceProviderAttachment(index: number) {
    if (this.providerFileBusy) { return; }
    this.replaceIndex = index;
    (this.$refs as any).providerFile.value = '';
    (this.$refs as any).providerFile.click();
  }

  public showeditProviderDialog() {
    this.editProviderDialog = true;
  }

  public hideEditProviderDialog() {
    this.editProviderDialog = false;
  }

  public async editProvider() {
    this.editProviderDialog = false;
    if (!this.validateProviderAttachmentsOnSubmit()) { return; }

    this.provider.enterpriseUnifiedNumber = String(this.provider.enterpriseUnifiedNumber || '').replace(/\s+/g, '').trim();
    this.provider.accreditationNumber = String(this.provider.accreditationNumber || '').replace(/\s+/g, '').trim();
    this.provider.taxNumber = String(this.provider.taxNumber || '').replace(/\s+/g, '').trim();

    this.provider.providerAttachmentModel = this.providerUploadedAttachments.map((a) => ({
      id: a.id,
      fileName: a.fileName,
      fileContent: a.fileContent,
      attachmentType: a.categoryId,
    }));

    this.provider.name = this.provider.name.trim();
    this.provider.enterpriseUnifiedNumber = this.provider.enterpriseUnifiedNumber.trim();
    this.provider.accreditationNumber = this.provider.accreditationNumber.trim();
    this.provider.taxNumber = this.provider.taxNumber.trim();

    const response = await this.providerService.Put(this.provider);
    if (response.isSuccess && response.result) {
      Toaster.success(this.$t('shared.updatedProviderSuccessfully').toString());
      this.$router.push({ name: 'ProviderSupport' });
    } else {
      Toaster.error(response.errorMessage);
    }
  }

  public isTaxNumberPaste(ev: ClipboardEvent) {
    const anyEv = ev as any;
    const cd: any = (anyEv && anyEv.clipboardData) ? anyEv.clipboardData : (window as any).clipboardData;
    let txt = '';
    if (cd && typeof cd.getData === 'function') {
      txt = cd.getData('text') || cd.getData('Text') || '';
    }
    if (!/^[0-9]+$/.test(txt)) {
      ev.preventDefault();
    }
  }

  public isNumberField(e: KeyboardEvent) {
    if (!this.validationService.isNumber((e as any).key)) { e.preventDefault(); }
  }

  public blockSpaces(e: KeyboardEvent) {
    const k = (e as any).key || '';
    const code = (e as any).keyCode || (e as any).which;
    if (k === ' ' || /\s/.test(k) || code === 32) {
      e.preventDefault();
    }
  }

  public stripSpaces(field: 'enterpriseUnifiedNumber' | 'accreditationNumber' | 'taxNumber') {
    const cur = (this.provider as any)[field] || '';
    (this.provider as any)[field] = String(cur).replace(/\s+/g, '');
  }

  public sanitizePaste(ev: ClipboardEvent, field: 'enterpriseUnifiedNumber' | 'accreditationNumber' | 'taxNumber') {
    const anyEv = ev as any;
    const cd: any = (anyEv && anyEv.clipboardData) ? anyEv.clipboardData : (window as any).clipboardData;
    let txt = '';
    if (cd && typeof cd.getData === 'function') {
      txt = cd.getData('text') || cd.getData('Text') || '';
    }

    if (/\s/.test(txt)) {
      ev.preventDefault();
      const cleaned = String(txt).replace(/\s+/g, '');
      const cur = (this.provider as any)[field] || '';
      (this.provider as any)[field] = String(cur) + cleaned;
    }
  }

  private resetProviderFileSelection() {
    (this.$refs as any).providerFile.value = '';
    this.selectedProviderFileName = '';
    this.selectedProviderFileContent = '';
    this.selectedProviderFileSize = 0;
    this.selectedProviderFileExt = '';
  }

  private validateProviderAttachmentsOnSubmit(): boolean {
    if (!this.hasAllProviderRequiredAttachments) {
      Toaster.error(this.$t('shared.attachmentsRequired').toString());
      return false;
    }
    let ok = true;
    for (let i = 0; i < this.providerUploadedAttachments.length; i++) {
      const a = this.providerUploadedAttachments[i];
      const ext = (a.fileExt || '').toLowerCase();
      if (!(ext === 'pdf' || ext === 'docx')) {
        ok = false;
        Toaster.error(this.$t('shared.invalidFileType').toString());
      }
      if (a.fileSize > 3000000) {
        ok = false;
        Toaster.error(this.$t('shared.fileSizeExceedsTheLimit').toString());
      }
    }
    return ok;
  }

  private categoryNameFor(id: string): string {
    const fromList =
      this.providerAttachmentCategoryList.find((x) => x.id === id) ||
      this.deletedProviderAttachmentCategoryList.find((x) => x.id === id);

    if (fromList) { return fromList.name; }

    const ZERO = '00000000-0000-0000-0000-000000000000';
    if (id === ZERO) { return this.$t('shared.delegationLetter').toString(); }
    return id;
  }


}
